import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { supabase } from "../lib/supabase";

/** PostgREST may return UUIDs as strings; normalize so we never drop valid IDs. */
function optionalNonEmptyString(value: unknown): string | undefined {
  if (value == null || value === "") return undefined;
  const s = typeof value === "string" ? value : String(value);
  const t = s.trim();
  return t.length > 0 ? t : undefined;
}

interface CompanyContextType {
  selectedCompany: number | null;
  companyDetails?: {
    id?: number;
    status: string;
    name?: string;
    street?: string;
    house_number?: string;
    moneybird_contact_id?: string;
    invoice_email?: string;
    pricing_payment_term?: number;
    leancustoms_organization_id?: string;
    leancustoms_contract_id?: string;
    picqer_enabled?: boolean;
    picqer_manual_orders_enabled?: boolean;
    wms_provider?: 'picqer' | 'channeldock' | null;
    channeldock_enabled?: boolean;
    api_access_granted?: boolean;
    api_access_requested_at?: string | null;
  };
  companyDetailsLoading: boolean;
  setSelectedCompany: (id: number) => void;
  refetchCompanyDetails: () => Promise<void>;
}

export const CompanyContext = createContext<CompanyContextType | undefined>(
  undefined
);

export const CompanyProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [selectedCompany, setSelectedCompany] = useState<number | null>(() => {
    const saved = localStorage.getItem("selectedCompany");
    return saved ? parseInt(saved) : null;
  });

  const [companyDetails, setCompanyDetails] = useState<{
    id?: number;
    status: string;
    name?: string;
    street?: string;
    house_number?: string;
    moneybird_contact_id?: string;
    invoice_email?: string;
    pricing_payment_term?: number;
    leancustoms_organization_id?: string;
    leancustoms_contract_id?: string;
    picqer_enabled?: boolean;
    picqer_manual_orders_enabled?: boolean;
    wms_provider?: 'picqer' | 'channeldock' | null;
    channeldock_enabled?: boolean;
    api_access_granted?: boolean;
    api_access_requested_at?: string | null;
  }>({
    status: "active",
    name: "Your Company",
    street: "",
    house_number: "",
    moneybird_contact_id: undefined,
    invoice_email: undefined,
    pricing_payment_term: undefined,
  });

  const [companyDetailsLoading, setCompanyDetailsLoading] = useState(false);

  // Memoize fetch company details function to prevent unnecessary recreations
  const fetchCompanyDetails = useCallback(async (companyId: number) => {
    setCompanyDetailsLoading(true);
    try {
      // Use * so optional columns (e.g. invoice_email, api_access_*) that are not
      // migrated yet do not cause PostgREST 400 on explicit select lists.
      const first = await supabase
        .from("company")
        .select("*")
        .eq("id", companyId)
        .single();

      const { data, error } = first;

      if (error) {
        console.error("Error fetching company details:", error);

        // Handle specific error cases
        if (
          error.code === "PGRST116" ||
          error.message?.includes(
            "JSON object requested, multiple (or no) rows returned"
          )
        ) {
          console.warn(
            `Company with ID ${companyId} not found. Using default company details.`
          );
          // Company not found - could be deleted or user doesn't have access
          localStorage.removeItem("selectedCompany");
          setSelectedCompany(null);
        } else if (error.code === "42P01") {
          // PostgreSQL code for "relation does not exist"
          console.log(
            "Company table does not exist yet, using default company name"
          );
        }

        setCompanyDetails({
          status: "active",
          name: "Your Company",
          street: "",
          house_number: "",
          moneybird_contact_id: undefined,
          invoice_email: undefined,
          pricing_payment_term: undefined,
          leancustoms_organization_id: undefined,
        });
        return;
      }

      if (data) {
        setCompanyDetails({
          id: data.id,
          status: data.status || "active",
          name: data.name || "Your Company",
          street: data.street || "",
          house_number: data.house_number || "",
          moneybird_contact_id: data.moneybird_contact_id || undefined,
          invoice_email: data.invoice_email || undefined,
          pricing_payment_term: data.pricing_payment_term || undefined,
          leancustoms_organization_id: optionalNonEmptyString(
            data.leancustoms_organization_id
          ),
          leancustoms_contract_id: optionalNonEmptyString(
            data.leancustoms_contract_id
          ),
          picqer_enabled: data.picqer_enabled ?? undefined,
          picqer_manual_orders_enabled: data.picqer_manual_orders_enabled ?? undefined,
          wms_provider: data.wms_provider ?? undefined,
          channeldock_enabled: data.channeldock_enabled ?? undefined,
          api_access_granted: (data as { api_access_granted?: boolean | null })
            .api_access_granted ?? undefined,
          api_access_requested_at: (
            data as { api_access_requested_at?: string | null }
          ).api_access_requested_at ?? undefined,
        });
      } else {
        // Fallback if no data is returned
        setCompanyDetails({
          status: "active",
          name: "Your Company",
          street: "",
          house_number: "",
          moneybird_contact_id: undefined,
          invoice_email: undefined,
          pricing_payment_term: undefined,
          leancustoms_organization_id: undefined,
        });
      }
    } catch (error) {
      console.error("Error in company details fetch:", error);
      setCompanyDetails({
        status: "active",
        name: "Your Company",
        street: "",
        house_number: "",
        moneybird_contact_id: undefined,
        invoice_email: undefined,
        pricing_payment_term: undefined,
        leancustoms_organization_id: undefined,
      });
    } finally {
      setCompanyDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyDetails(selectedCompany);
    } else {
      // Set default company details if no company is selected
      setCompanyDetails({
        status: "active",
        name: "Your Company",
        street: "",
        house_number: "",
        moneybird_contact_id: undefined,
        invoice_email: undefined,
        pricing_payment_term: undefined,
        leancustoms_organization_id: undefined,
      });
    }
  }, [selectedCompany, fetchCompanyDetails]);

  // Refresh when the user returns to the tab so Lean Customs / admin edits are reflected
  // without requiring a full reload (sidebar and /customs read from this cache).
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (selectedCompany) {
        void fetchCompanyDetails(selectedCompany);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [selectedCompany, fetchCompanyDetails]);

  const handleCompanyChange = useCallback((id: number) => {
    setSelectedCompany(id);
    localStorage.setItem("selectedCompany", id.toString());
  }, []);

  // Add a refetch function that can be called from components
  const refetchCompanyDetails = useCallback(async () => {
    if (selectedCompany) {
      await fetchCompanyDetails(selectedCompany);
    }
  }, [selectedCompany, fetchCompanyDetails]);

  // Update the context value to include the refetch function
  const contextValue = useMemo(
    () => ({
      selectedCompany,
      companyDetails,
      companyDetailsLoading,
      setSelectedCompany: handleCompanyChange,
      refetchCompanyDetails,
    }),
    [
      selectedCompany,
      companyDetails,
      companyDetailsLoading,
      handleCompanyChange,
      refetchCompanyDetails,
    ]
  );

  return (
    <CompanyContext.Provider value={contextValue}>
      {children}
    </CompanyContext.Provider>
  );
};
